"use client";

/**
 * Element-synced media engine for ClipVideoPlayer.
 * Snapshot is read from the <video> on every media event; chrome derives from it.
 */

import type { RefObject, SyntheticEvent, VideoHTMLAttributes } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import type { ClipMediaSnapshot } from "./clip-video-player-types";
import { EMPTY_CLIP_MEDIA_SNAPSHOT } from "./clip-video-player-types";
import { clampMediaTime } from "./derive-clip-media-ui";

interface EngineFlags {
  waiting: boolean;
  started: boolean;
}

function bufferedEndOf(video: HTMLVideoElement): number {
  if (video.buffered.length === 0) return 0;
  return video.buffered.end(video.buffered.length - 1);
}

function readSnapshot(
  video: HTMLVideoElement,
  flags: EngineFlags,
): ClipMediaSnapshot {
  return {
    paused: video.paused,
    ended: video.ended,
    seeking: video.seeking,
    waiting: flags.waiting,
    readyState: video.readyState,
    currentTime: video.currentTime,
    duration: Number.isFinite(video.duration) ? video.duration : 0,
    bufferedEnd: bufferedEndOf(video),
    volume: video.volume,
    muted: video.muted,
    playbackRate: video.playbackRate,
    started: flags.started,
    hasError: video.error !== null,
  };
}

export interface ClipMediaEngine {
  readonly snapshot: ClipMediaSnapshot;
  readonly videoHandlers: Pick<
    VideoHTMLAttributes<HTMLVideoElement>,
    | "onPlay"
    | "onPause"
    | "onPlaying"
    | "onWaiting"
    | "onSeeking"
    | "onSeeked"
    | "onCanPlay"
    | "onTimeUpdate"
    | "onDurationChange"
    | "onLoadedMetadata"
    | "onProgress"
    | "onVolumeChange"
    | "onRateChange"
    | "onEmptied"
    | "onAbort"
    | "onEnded"
    | "onLoadedData"
    | "onError"
  >;
  togglePlay: () => void;
  seekTo: (time: number) => void;
  skip: (delta: number) => void;
  toggleMute: () => void;
  changeVolume: (next: number) => void;
  setPlaybackRate: (next: number) => void;
}

export function useClipMediaEngine(
  videoRef: RefObject<HTMLVideoElement | null>,
  options: {
    readonly src: string;
    readonly onError?: () => void;
    readonly onLoadedData?: () => void;
  },
): ClipMediaEngine {
  const flagsRef = useRef<EngineFlags>({ waiting: false, started: false });
  const pendingSeekRef = useRef<number | null>(null);
  const onErrorRef = useRef(options.onError);
  const onLoadedDataRef = useRef(options.onLoadedData);
  onErrorRef.current = options.onError;
  onLoadedDataRef.current = options.onLoadedData;
  const [snapshot, setSnapshot] = useState<ClipMediaSnapshot>(
    EMPTY_CLIP_MEDIA_SNAPSHOT,
  );

  const syncFromVideo = useCallback(
    (video: HTMLVideoElement, patch?: Partial<EngineFlags>): void => {
      if (patch) {
        flagsRef.current = { ...flagsRef.current, ...patch };
      }
      setSnapshot(readSnapshot(video, flagsRef.current));
    },
    [],
  );

  // New src must not keep the previous clip's times/flags for even one frame.
  useEffect(() => {
    flagsRef.current = { waiting: false, started: false };
    pendingSeekRef.current = null;
    const video = videoRef.current;
    if (video) {
      setSnapshot(readSnapshot(video, flagsRef.current));
    } else {
      setSnapshot(EMPTY_CLIP_MEDIA_SNAPSHOT);
    }
  }, [options.src, videoRef]);

  const clearWaiting = useCallback(
    (video: HTMLVideoElement): void => {
      syncFromVideo(video, { waiting: false });
    },
    [syncFromVideo],
  );

  const onMediaEvent = useCallback(
    (event: SyntheticEvent<HTMLVideoElement>): void => {
      syncFromVideo(event.currentTarget);
    },
    [syncFromVideo],
  );

  const togglePlay = useCallback((): void => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      void video.play().catch(() => {
        syncFromVideo(video, { waiting: false });
      });
    } else {
      video.pause();
    }
  }, [videoRef, syncFromVideo]);

  const seekTo = useCallback(
    (time: number): void => {
      const video = videoRef.current;
      if (!video) return;
      const target = clampMediaTime(time, video.duration);
      // Seeks are serialized: an element-level seek while one is in flight
      // restarts Chrome's pipeline mid-fetch, and overlapping seeks into
      // unbuffered ranges are what trip its decoder (PIPELINE_ERROR_DECODE).
      // Only the newest target is kept; `onSeeked` issues it once the current
      // seek settles, so scrub-spam degrades to a short seek chain.
      if (video.seeking) {
        pendingSeekRef.current = target;
        syncFromVideo(video);
        return;
      }
      video.currentTime = target;
      syncFromVideo(video);
    },
    [videoRef, syncFromVideo],
  );

  const skip = useCallback(
    (delta: number): void => {
      const video = videoRef.current;
      if (!video) return;
      seekTo(video.currentTime + delta);
    },
    [videoRef, seekTo],
  );

  const toggleMute = useCallback((): void => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = !video.muted;
    syncFromVideo(video);
  }, [videoRef, syncFromVideo]);

  const changeVolume = useCallback(
    (next: number): void => {
      const video = videoRef.current;
      if (!video) return;
      const v = Math.min(1, Math.max(0, next));
      video.volume = v;
      video.muted = v === 0;
      syncFromVideo(video);
    },
    [videoRef, syncFromVideo],
  );

  const setPlaybackRate = useCallback(
    (next: number): void => {
      const video = videoRef.current;
      if (!video) return;
      video.playbackRate = next;
      syncFromVideo(video);
    },
    [videoRef, syncFromVideo],
  );

  const videoHandlers: ClipMediaEngine["videoHandlers"] = {
    onPlay: (event) => {
      syncFromVideo(event.currentTarget, { started: true });
    },
    onPause: (event) => {
      // Pause must not leave chrome gated on waiting — clear sticky waiting.
      syncFromVideo(event.currentTarget, { waiting: false });
    },
    onPlaying: (event) => {
      clearWaiting(event.currentTarget);
    },
    onWaiting: (event) => {
      syncFromVideo(event.currentTarget, { waiting: true });
    },
    onSeeking: onMediaEvent,
    onSeeked: (event) => {
      const video = event.currentTarget;
      const pending = pendingSeekRef.current;
      pendingSeekRef.current = null;
      // Flush the newest queued target — skipped when the settled position
      // already is (about) the queued one.
      if (pending !== null && Math.abs(video.currentTime - pending) > 0.25) {
        video.currentTime = pending;
      }
      clearWaiting(video);
    },
    onCanPlay: (event) => {
      if (!event.currentTarget.paused) {
        clearWaiting(event.currentTarget);
      } else {
        syncFromVideo(event.currentTarget);
      }
    },
    onTimeUpdate: onMediaEvent,
    onDurationChange: onMediaEvent,
    onLoadedMetadata: onMediaEvent,
    onProgress: onMediaEvent,
    onVolumeChange: onMediaEvent,
    onRateChange: onMediaEvent,
    onEnded: onMediaEvent,
    onEmptied: (event) => {
      flagsRef.current = { waiting: false, started: false };
      pendingSeekRef.current = null;
      syncFromVideo(event.currentTarget);
    },
    onAbort: (event) => {
      flagsRef.current = { waiting: false, started: flagsRef.current.started };
      syncFromVideo(event.currentTarget);
    },
    onLoadedData: (event) => {
      syncFromVideo(event.currentTarget);
      onLoadedDataRef.current?.();
    },
    onError: (event) => {
      syncFromVideo(event.currentTarget);
      onErrorRef.current?.();
    },
  };

  return {
    snapshot,
    videoHandlers,
    togglePlay,
    seekTo,
    skip,
    toggleMute,
    changeVolume,
    setPlaybackRate,
  };
}
